import React, { useState, useEffect, useCallback } from "react";
import { FaCommentDots, FaPlusCircle, FaMinusCircle, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./Timesheet_entry.css";
import { apiFetch } from "./utils/api";
import dnPopup from "./utils/dnPopup";
import logo_eagle from "./assets/img/logo_eagle.png";

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const STANDARD_HOLIDAY_HOURS = 8;
const LOCATIONS = ["Hyderabad", "Chennai", "Pune", "Nagpur"];

// Monday of the week that contains `date`
const getMonday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const offset = (d.getDay() + 6) % 7; // Mon=0 .. Sun=6
    d.setDate(d.getDate() - offset);
    return d;
};

const toLocalYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
};

const shortDate = (date) =>
    date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

let rowSeq = 1;
const blankRow = () => ({
    id: rowSeq++,
    project_id: "",
    project_name: "",
    task_id: "",
    task_desc: "",
    onsite_offshore: "OF",
    client_billable: "Billable",
    billing_location: "",
    hours: [0, 0, 0, 0, 0, 0, 0],
    comments: "",
    tasks: [],
});

const Timesheet_entry = ({ navSize }) => {
    const [weekStart, setWeekStart] = useState(getMonday(new Date()));
    const [projects, setProjects] = useState([]);
    const [projectsLoaded, setProjectsLoaded] = useState(false); // fetch finished (even if empty)
    const [rows, setRows] = useState([blankRow()]);
    const [timeOffHours, setTimeOffHours] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [punchHours, setPunchHours] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [saving, setSaving] = useState(false);

    // Approval workflow state for the selected week
    const [status, setStatus] = useState("Draft");   // Draft | Submitted | Approved | Rejected
    const [rejectReason, setRejectReason] = useState(null);

    // Submitted / Approved weeks are read-only. Rejected reopens for editing.
    const locked = status === "Submitted" || status === "Approved";
    // Only a FULL day off (holiday / full-day approved leave = 8h) blocks project
    // hours. A half-day leave (4h) still leaves room to log the worked half.
    const blockedDay = (i) => Number(timeOffHours[i]) >= STANDARD_HOLIDAY_HOURS;

    // comment modal
    const [commentRowId, setCommentRowId] = useState(null);
    const [commentDraft, setCommentDraft] = useState("");

    const weekStartYMD = toLocalYMD(weekStart);
    const weekDates = [...Array(7)].map((_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
    });
    const weekEndExclusive = new Date(weekStart);
    weekEndExclusive.setDate(weekEndExclusive.getDate() + 7);

    // A timesheet can't be filled for a week that hasn't happened. The current
    // week is the furthest you may navigate to or submit.
    const currentWeekStartYMD = toLocalYMD(getMonday(new Date()));
    const isFutureWeek = weekStartYMD > currentWeekStartYMD;
    const atCurrentWeek = weekStartYMD >= currentWeekStartYMD;

    // ---- Fetches -------------------------------------------------------------
    const fetchProjects = async () => {
        try {
            const res = await apiFetch("/api/Project_configuration/all", { credentials: "include" });
            const data = await res.json();
            setProjects(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching projects:", err);
        } finally {
            // Mark the fetch as done even when there are zero projects — the week
            // (status, saved rows, holiday/leave time off) must still load.
            setProjectsLoaded(true);
        }
    };

    const fetchTasks = async (project_id) => {
        try {
            const res = await apiFetch(`/api/Project_configuration/tasks/${project_id}`, { credentials: "include" });
            const data = await res.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error("Error fetching tasks:", err);
            return [];
        }
    };

    // Punch hours for the selected week, Monday-first
    const fetchPunch = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/punch/history`, { credentials: "include" });
            const data = await res.json();
            const hours = Array(7).fill(0);
            (Array.isArray(data) ? data : []).forEach((e) => {
                if (!e.punch_in || !e.punch_out) return;
                const inD = new Date(e.punch_in);
                if (inD >= weekStart && inD < weekEndExclusive) {
                    const idx = (inD.getDay() + 6) % 7; // Mon=0..Sun=6
                    hours[idx] += (new Date(e.punch_out) - inD) / 3600000;
                }
            });
            setPunchHours(hours.map((h) => Math.round(h * 100) / 100));
        } catch (err) {
            console.error("Error fetching punch history:", err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStartYMD]);

    // Auto-fill the Time Off row from holidays AND approved leaves for the week
    const fetchTimeOffAutofill = useCallback(async () => {
        const auto = Array(7).fill(0);
        const dayIdx = (d) => (d.getDay() + 6) % 7; // Mon=0..Sun=6
        const inWeek = (d) => d >= weekStart && d < weekEndExclusive;

        // --- Holidays ---
        try {
            const months = new Set([
                `${weekStart.getMonth() + 1}-${weekStart.getFullYear()}`,
                `${weekDates[6].getMonth() + 1}-${weekDates[6].getFullYear()}`,
            ]);
            const all = [];
            for (const key of months) {
                const [m, y] = key.split("-");
                const res = await apiFetch(`/api/holiday/list?month=${m}&year=${y}`, { credentials: "include" });
                const data = await res.json();
                if (Array.isArray(data)) all.push(...data);
            }
            all.forEach((h) => {
                const start = new Date(h.holiday_date);
                const end = h.holiday_end ? new Date(h.holiday_end) : new Date(start);
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0); // normalize like the leave branch does
                let cur = new Date(start);
                while (cur <= end) {
                    if (inWeek(cur)) auto[dayIdx(cur)] = STANDARD_HOLIDAY_HOURS;
                    cur.setDate(cur.getDate() + 1);
                }
            });
        } catch (err) {
            console.error("Error fetching holidays:", err);
        }

        // --- Approved leaves (full day = 8h, half day = 4h) ---
        try {
            const res = await apiFetch(`/api/leave/my_total_leaves`, { credentials: "include" });
            const leaves = await res.json();
            (Array.isArray(leaves) ? leaves : [])
                .filter((l) => l.leave_status === "Approved")
                .forEach((l) => {
                    const start = new Date(l.start_date); start.setHours(0, 0, 0, 0);
                    const end = new Date(l.end_date); end.setHours(0, 0, 0, 0);
                    let cur = new Date(start);
                    while (cur <= end) {
                        if (inWeek(cur)) {
                            const isStart = cur.getTime() === start.getTime();
                            const isEnd = cur.getTime() === end.getTime();
                            let hrs = STANDARD_HOLIDAY_HOURS;
                            if (isStart && l.start_date_breakdown && l.start_date_breakdown !== "Full") hrs = STANDARD_HOLIDAY_HOURS / 2;
                            if (isEnd && l.end_date_breakdown && l.end_date_breakdown !== "Full") hrs = STANDARD_HOLIDAY_HOURS / 2;
                            auto[dayIdx(cur)] = Math.max(auto[dayIdx(cur)], hrs);
                        }
                        cur.setDate(cur.getDate() + 1);
                    }
                });
        } catch (err) {
            console.error("Error fetching approved leaves:", err);
        }

        return auto;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStartYMD]);

    // Load saved rows for the week (and hydrate task lists / autofill time off)
    const loadWeek = useCallback(async () => {
        try {
            // Workflow status (Draft / Submitted / Approved / Rejected)
            let weekStatus = "Draft";
            try {
                const sRes = await apiFetch(`/api/timesheet/status?week_start=${weekStartYMD}`, { credentials: "include" });
                const sData = await sRes.json();
                weekStatus = sData.status || "Draft";
                setStatus(weekStatus);
                setRejectReason(sData.reject_reason || null);
            } catch {
                setStatus("Draft");
                setRejectReason(null);
            }

            const res = await apiFetch(`/api/timesheet/week?week_start=${weekStartYMD}`, { credentials: "include" });
            const saved = await res.json();
            const savedRows = Array.isArray(saved) ? saved : [];

            const workSaved = savedRows.filter((r) => r.entry_type !== "timeoff");
            const timeOffSaved = savedRows.find((r) => r.entry_type === "timeoff");

            if (workSaved.length > 0) {
                const hydrated = await Promise.all(
                    workSaved.map(async (r) => {
                        const tasks = r.project_id ? await fetchTasks(r.project_id) : [];
                        const proj = projects.find((p) => p.project_id === r.project_id);
                        const task = tasks.find((t) => t.task_id === r.task_id);
                        return {
                            id: rowSeq++,
                            project_id: r.project_id || "",
                            project_name: proj ? proj.project_name : "",
                            task_id: r.task_id || "",
                            task_desc: task ? task.task_desc : "",
                            onsite_offshore: r.onsite_offshore || "OF",
                            client_billable: r.client_billable || "Billable",
                            billing_location: r.billing_location || "",
                            hours: [r.mon_hours, r.tue_hours, r.wed_hours, r.thu_hours, r.fri_hours, r.sat_hours, r.sun_hours].map(Number),
                            comments: r.comments || "",
                            tasks,
                        };
                    })
                );
                setRows(hydrated);
            } else {
                setRows([blankRow()]);
            }

            // A locked week keeps the exact snapshot the approver reviewed. An
            // EDITABLE week always recomputes, so a holiday created — or a leave
            // approved — after a draft was saved still shows up here.
            const lockedWeek = weekStatus === "Submitted" || weekStatus === "Approved";
            if (lockedWeek && timeOffSaved) {
                setTimeOffHours(
                    [timeOffSaved.mon_hours, timeOffSaved.tue_hours, timeOffSaved.wed_hours, timeOffSaved.thu_hours,
                    timeOffSaved.fri_hours, timeOffSaved.sat_hours, timeOffSaved.sun_hours].map(Number)
                );
            } else {
                const auto = await fetchTimeOffAutofill();
                setTimeOffHours(auto);
            }
        } catch (err) {
            console.error("Error loading timesheet:", err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStartYMD, projects]);

    useEffect(() => { fetchProjects(); }, []);
    useEffect(() => { fetchPunch(); }, [fetchPunch]);
    useEffect(() => { if (projectsLoaded) loadWeek(); }, [weekStartYMD, projectsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Force project hours to 0 on any holiday / approved-leave day (blocked)
    useEffect(() => {
        setRows((prev) => {
            let changed = false;
            const next = prev.map((r) => {
                let rowChanged = false;
                const hours = r.hours.map((h, i) => {
                    if (blockedDay(i) && Number(h) !== 0) { rowChanged = true; return 0; }
                    return h;
                });
                if (rowChanged) { changed = true; return { ...r, hours }; }
                return r;
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeOffHours]);

    // ---- Row helpers ---------------------------------------------------------
    const updateRow = (id, patch) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const onProjectChange = async (id, project_id) => {
        const proj = projects.find((p) => p.project_id === project_id);
        updateRow(id, { project_id, project_name: proj ? proj.project_name : "", task_id: "", task_desc: "", tasks: [] });
        if (project_id) {
            const tasks = await fetchTasks(project_id);
            updateRow(id, { tasks });
        }
    };

    const onTaskChange = (id, task_id) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const task = r.tasks.find((t) => t.task_id === task_id);
                return { ...r, task_id, task_desc: task ? task.task_desc : "" };
            })
        );
    };

    const onHourChange = (id, dayIdx, value) => {
        if (locked || blockedDay(dayIdx)) return; // locked week or holiday/leave day
        let v = value === "" ? 0 : parseFloat(value);
        if (isNaN(v) || v < 0) v = 0;
        if (v > 24) v = 24;
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const hours = [...r.hours];
                hours[dayIdx] = v;
                return { ...r, hours };
            })
        );
    };

    const addRow = () => setRows((prev) => [...prev, blankRow()]);
    const removeRow = (id) =>
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

    const rowTotal = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);
    const colTotal = (dayIdx) =>
        rows.reduce((a, r) => a + (Number(r.hours[dayIdx]) || 0), 0) + (Number(timeOffHours[dayIdx]) || 0);
    const grandTotal = rows.reduce((a, r) => a + rowTotal(r.hours), 0) + rowTotal(timeOffHours);

    // A day is "over limit" (flagged red) when work + time-off exceeds capacity:
    //  - partial leave / holiday day (0 < off < 8h): total must stay within 8h
    //    (e.g. a half-day leave = 4h leaves room for only 4h of work)
    //  - normal day: overtime is allowed, so only impossible totals (> 24h) flag
    const dayOverLimit = (i) => {
        const off = Number(timeOffHours[i]) || 0;
        const total = colTotal(i);
        if (off > 0 && off < STANDARD_HOLIDAY_HOURS) return total > STANDARD_HOLIDAY_HOURS;
        return total > 24;
    };
    const hasOverLimit = DAY_LABELS.some((_, i) => dayOverLimit(i));

    const fmt = (n) => {
        const v = Number(n) || 0;
        return Number.isInteger(v) ? String(v) : v.toFixed(2);
    };

    // Format decimal hours as "8h 30m" (read-only punch row) so it isn't
    // misread as a clock time — e.g. 8.5 -> "8h 30m", not "8.50".
    const fmtHM = (n) => {
        const totalMin = Math.round((Number(n) || 0) * 60);
        if (totalMin === 0) return "0";
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h && m) return `${h}h ${m}m`;
        return h ? `${h}h` : `${m}m`;
    };

    // ---- Comment modal -------------------------------------------------------
    const openComment = (row) => { setCommentRowId(row.id); setCommentDraft(row.comments || ""); };
    const saveComment = () => { updateRow(commentRowId, { comments: commentDraft }); setCommentRowId(null); };

    // ---- Week nav ------------------------------------------------------------
    const shiftWeek = (deltaDays) => {
        if (deltaDays > 0 && atCurrentWeek) return; // never navigate into a future week
        const d = new Date(weekStart);
        d.setDate(d.getDate() + deltaDays);
        setWeekStart(getMonday(d));
    };

    // ---- Save / Submit -------------------------------------------------------
    const buildPayload = () => {
        // Keep any row that has a project OR any hours typed in. Filtering on
        // project alone silently discarded hours typed before a project was
        // picked — a Save Draft must never lose work in progress.
        const workRows = rows
            .filter((r) => r.project_id || r.hours.some((h) => Number(h) > 0))
            .map((r) => ({
                entry_type: "work",
                project_id: r.project_id,
                task_id: r.task_id,
                onsite_offshore: r.onsite_offshore,
                client_billable: r.client_billable,
                billing_location: r.billing_location,
                hours: r.hours.map((h, i) => (blockedDay(i) ? 0 : Number(h) || 0)),
                comments: r.comments,
            }));

        return {
            week_start: weekStartYMD,
            rows: [
                ...workRows,
                { entry_type: "timeoff", hours: timeOffHours, comments: null },
            ],
        };
    };

    const handleSaveDraft = async () => {
        if (locked) return;
        setSaving(true);
        try {
            const res = await apiFetch("/api/timesheet/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildPayload()),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                // A rejected week stays Rejected (reason still shown) until it is
                // actually resubmitted — saving a draft must not hide the reason.
                setStatus(data.status || "Draft");
                setRejectReason(data.reject_reason || null);
                dnPopup.success("Draft Saved", "Your timesheet draft has been saved.");
            } else {
                dnPopup.error("Save Failed", data.message || "Something went wrong.");
            }
        } catch (err) {
            console.error("Save error:", err);
            dnPopup.error("Error", "Failed to save timesheet.");
        } finally {
            setSaving(false);
        }
    };

    // Working days that must be accounted for (Mon–Fri). Sat/Sun optional.
    const REQUIRED_DAYS = [0, 1, 2, 3, 4];

    const validateForSubmit = () => {
        // 0) A week that hasn't happened yet can't be submitted
        if (isFutureWeek) {
            return "You cannot submit a timesheet for a future week.";
        }

        // 1) Every required day needs work hours OR holiday/time off
        const missingDays = REQUIRED_DAYS.filter((i) => colTotal(i) <= 0).map((i) => DAY_LABELS[i]);
        if (missingDays.length) {
            return `Add work hours or time off for: ${missingDays.join(", ")}.`;
        }

        // 2) Any row that has hours must have project, task and billing location
        for (const r of rows) {
            const hasHours = r.hours.some((h, i) => !blockedDay(i) && Number(h) > 0);
            if (!hasHours) continue;
            if (!r.project_id) return "A row has hours but no project selected. Please pick a project or clear the hours.";
            if (!r.task_id) return `Select a Task for project ${r.project_id}.`;
            if (!r.billing_location) return `Select a Billing Location for project ${r.project_id}.`;
        }
        return null;
    };

    const handleSubmit = async () => {
        if (locked) return;

        const problem = validateForSubmit();
        if (problem) {
            dnPopup.warning("Incomplete timesheet", problem);
            return;
        }

        const confirm = await dnPopup.confirm({
            title: "Submit timesheet?",
            message: "Once submitted, this week will be locked until your approver reviews it.",
            confirmLabel: "Yes, Submit",
        });
        if (!confirm) return;

        setSaving(true);
        try {
            const res = await apiFetch("/api/timesheet/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildPayload()),
                credentials: "include",
            });
            const data = await res.json();
            if (data.success) {
                setStatus("Submitted");
                setRejectReason(null);
                dnPopup.success("Submitted", "Your timesheet has been sent for approval.");
            } else {
                dnPopup.error("Submit Failed", data.message || "Something went wrong.");
            }
        } catch (err) {
            console.error("Submit error:", err);
            dnPopup.error("Error", "Failed to submit timesheet.");
        } finally {
            setSaving(false);
        }
    };

    // Status banner text + class
    const STATUS_META = {
        Draft: { label: "Draft", cls: "ts-badge-draft" },
        Submitted: { label: "Submitted — pending approval", cls: "ts-badge-submitted" },
        Approved: { label: "Approved", cls: "ts-badge-approved" },
        Rejected: { label: "Rejected", cls: "ts-badge-rejected" },
    };

    return (
        <div className="timesheet-container">
            <div className={`timesheet-dashboard ${navSize}`}>
                <div className="timesheet-config-content">
                    <h1>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Timesheet Entry
                    </h1>

                    {/* Week navigation + status + actions */}
                    <div className="ts-toolbar">
                        <div className="ts-week-nav">
                            <button className="ts-week-btn" onClick={() => shiftWeek(-7)} title="Previous week">
                                <FaChevronLeft />
                            </button>
                            <span className="ts-week-label">
                                {shortDate(weekDates[0])} – {shortDate(weekDates[6])}, {weekDates[6].getFullYear()}
                            </span>
                            <button
                                className="ts-week-btn"
                                onClick={() => shiftWeek(7)}
                                disabled={atCurrentWeek}
                                title={atCurrentWeek ? "You're on the current week" : "Next week"}
                                style={atCurrentWeek ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                            >
                                <FaChevronRight />
                            </button>
                        </div>

                        <div className="ts-toolbar-right">
                            <span className={`ts-status-badge ${STATUS_META[status]?.cls || ""}`}>
                                {STATUS_META[status]?.label || status}
                            </span>
                            {!locked && (
                                <>
                                    <button className="ts-draft-btn" onClick={handleSaveDraft} disabled={saving}>
                                        {saving ? "Saving…" : "Save Draft"}
                                    </button>
                                    <button className="ts-save-btn" onClick={handleSubmit} disabled={saving}>
                                        {status === "Rejected" ? "Resubmit" : "Submit Timesheet"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {status === "Rejected" && rejectReason && (
                        <div className="ts-reject-banner">
                            <strong>Rejected:</strong> {rejectReason}
                            <span className="ts-reject-hint"> — edit and resubmit your timesheet.</span>
                        </div>
                    )}
                    {status === "Submitted" && (
                        <div className="ts-info-banner">
                            This timesheet has been submitted and is locked until your approver reviews it.
                        </div>
                    )}
                    {status === "Approved" && (
                        <div className="ts-approved-banner">
                            This timesheet has been approved and can no longer be edited.
                        </div>
                    )}
                    {hasOverLimit && (
                        <div className="ts-warn-banner">
                            <strong>Check the days in red:</strong> work hours plus leave/holiday time
                            exceed the allowed hours for that day (a half-day leave day allows only 4h of work).
                        </div>
                    )}

                    <div className="table-wrapper">
                        <table className="timesheet-table">
                            <thead>
                                <tr>
                                    <th>Project ID</th>
                                    <th>Project Name</th>
                                    <th>Task/Activity ID</th>
                                    <th>Task Description</th>
                                    <th>Onsite/Offshore</th>
                                    <th>Client Billable</th>
                                    <th>Billing Location</th>
                                    {DAY_LABELS.map((d, i) => (
                                        <th key={d} className="ts-day-th">
                                            {d}
                                            <span className="ts-day-date">{shortDate(weekDates[i])}</span>
                                        </th>
                                    ))}
                                    <th>Total</th>
                                    <th>Comments</th>
                                </tr>
                            </thead>

                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <select value={row.project_id} onChange={(e) => onProjectChange(row.id, e.target.value)} disabled={locked}>
                                                <option value="">Select Project</option>
                                                {projects.map((p) => (
                                                    <option key={p.project_id} value={p.project_id}>{p.project_id}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td><input type="text" value={row.project_name} placeholder="Project Name" readOnly /></td>
                                        <td>
                                            <select value={row.task_id} onChange={(e) => onTaskChange(row.id, e.target.value)} disabled={locked || !row.project_id}>
                                                <option value="">Select Task</option>
                                                {row.tasks.map((t) => (
                                                    <option key={t.task_id} value={t.task_id}>{t.task_id}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td><input type="text" value={row.task_desc} placeholder="Task Description" readOnly /></td>
                                        <td>
                                            <select value={row.onsite_offshore} onChange={(e) => updateRow(row.id, { onsite_offshore: e.target.value })} disabled={locked}>
                                                <option value="ON">ON</option>
                                                <option value="OF">OFF</option>
                                            </select>
                                        </td>
                                        <td>
                                            <select value={row.client_billable} onChange={(e) => updateRow(row.id, { client_billable: e.target.value })} disabled={locked}>
                                                <option value="Billable">Billable</option>
                                                <option value="Non-Billable">Non Billable</option>
                                            </select>
                                        </td>
                                        <td>
                                            <select value={row.billing_location} onChange={(e) => updateRow(row.id, { billing_location: e.target.value })} disabled={locked}>
                                                <option value="">Select Location</option>
                                                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                                            </select>
                                        </td>
                                        {row.hours.map((h, i) => (
                                            <td key={i}>
                                                <input
                                                    type="number"
                                                    className={`small-input${dayOverLimit(i) ? " ts-over-input" : ""}`}
                                                    min="0" max="24" step="0.5"
                                                    value={blockedDay(i) ? "" : (h === 0 ? "" : h)}
                                                    placeholder={blockedDay(i) ? "—" : "0"}
                                                    disabled={locked || blockedDay(i)}
                                                    title={dayOverLimit(i)
                                                        ? "Work + leave/holiday exceeds the allowed hours for this day"
                                                        : (blockedDay(i) ? "Holiday / approved leave" : "")}
                                                    onChange={(e) => onHourChange(row.id, i, e.target.value)}
                                                />
                                            </td>
                                        ))}
                                        <td className="ts-total-cell">{fmt(rowTotal(row.hours))}</td>
                                        <td className="comment-icons">
                                            <FaCommentDots
                                                className={`icon comment-icon ${row.comments ? "has-comment" : ""}`}
                                                title={row.comments || "Add comment"}
                                                onClick={() => !locked && openComment(row)}
                                                style={locked ? { opacity: 0.4, pointerEvents: "none" } : undefined}
                                            />
                                            {!locked && <FaPlusCircle className="icon add-icon" title="Add row" onClick={addRow} />}
                                            {!locked && <FaMinusCircle className="icon remove-icon" title="Remove row" onClick={() => removeRow(row.id)} />}
                                        </td>
                                    </tr>
                                ))}

                                {/* Column totals */}
                                <tr className="ts-totals-row">
                                    <td colSpan={7} style={{ fontWeight: 700, textAlign: "right" }}>Daily Total</td>
                                    {DAY_LABELS.map((_, i) => (
                                        <td
                                            key={i}
                                            className={dayOverLimit(i) ? "ts-over-total" : ""}
                                            style={{ textAlign: "center", fontWeight: 700 }}
                                        >
                                            {fmt(colTotal(i))}
                                        </td>
                                    ))}
                                    <td style={{ fontWeight: 700, textAlign: "center" }}>{fmt(grandTotal)}</td>
                                    <td></td>
                                </tr>

                                {/* Punch In / Punch Out (computed, read-only) */}
                                <tr className="punch-row">
                                    <td colSpan={7} className="punch-label" style={{ fontWeight: "bold" }}>Punch In / Punch Out</td>
                                    {punchHours.map((h, i) => (
                                        <td key={i} style={{ textAlign: "center", fontWeight: "bold" }}>{fmtHM(h)}</td>
                                    ))}
                                    <td style={{ fontWeight: "bold", textAlign: "center" }}>{fmtHM(rowTotal(punchHours))}</td>
                                    <td></td>
                                </tr>

                                {/* Holiday / Time Off (auto-filled from holidays + approved leaves, read-only) */}
                                <tr className="holiday-row">
                                    <td colSpan={7} className="ts-holiday-label" style={{ fontWeight: "bold" }}>
                                        Holiday / Time Off
                                        <span className="ts-holiday-hint"> (auto-filled)</span>
                                    </td>
                                    {timeOffHours.map((h, i) => (
                                        <td key={i}>
                                            <input
                                                type="number" className="small-input"
                                                value={h === 0 ? "" : h}
                                                placeholder="0"
                                                readOnly
                                                disabled
                                                title="Auto-filled from holidays and approved leaves"
                                            />
                                        </td>
                                    ))}
                                    <td style={{ fontWeight: "bold", textAlign: "center" }}>{fmt(rowTotal(timeOffHours))}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Comment modal */}
            {commentRowId !== null && (
                <div className="dz-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setCommentRowId(null); }}>
                    <div className="dz-modal" style={{ width: 460 }}>
                        <div className="dz-modal-header">
                            Comment
                            <button className="dz-modal-close" onClick={() => setCommentRowId(null)}>✕</button>
                        </div>
                        <div className="dz-modal-body">
                            <textarea
                                className="dz-textarea"
                                rows={4}
                                value={commentDraft}
                                placeholder="Add a comment for this row…"
                                onChange={(e) => setCommentDraft(e.target.value)}
                            />
                        </div>
                        <div className="dz-modal-footer">
                            <button className="dz-btn dz-btn-secondary" onClick={() => setCommentRowId(null)}>Cancel</button>
                            <button className="dz-btn dz-btn-primary" onClick={saveComment}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timesheet_entry;
